/**
 * Entitäten des neuen PAT-Systems als Zod-Schemas. Version 0 (23.10.2026).
 *
 * Grundlage: docs/JOBS_UND_DATEN.md, docs/REGELN_BESTAND.md, docs/APP_KONZEPT.md.
 * Besitzer: Claude (Manager). Änderungen über docs/ANFRAGEN.md.
 *
 * Konventionen:
 * - IDs sind Strings. Legacy-IDs bleiben Dezimalstrings, damit MySQL-Export und
 *   eBay-ItemIDs ohne Präzisionsverlust übernommen werden.
 * - Zeitpunkte sind ISO-8601-Strings in UTC (`...Z`).
 * - Geldbeträge sind Dezimalstrings, nie Floats.
 * - Keine personenbezogenen Freitexte außer kundeneigenen Inhalten (Artikel, Design) und dem
 *   Stammdatensatz `PROFILE` (Stufe H, 25.10.2026): Kontakt- und Rechnungsdaten des Kontos liegen
 *   ausschließlich dort, nie in META, nie in einem GSI, nie in `/me`.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Primitive
// ---------------------------------------------------------------------------

export const DecimalId = z.string().regex(/^[1-9][0-9]{0,19}$/, 'decimal id');
export const TenantId = DecimalId;
export const EbayItemId = DecimalId;
export const IsoDateTime = z.string().datetime({ offset: false });
/** Kalendertag ohne Zeit (YYYY-MM-DD). */
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const Money = z.object({
  amount: z.string().regex(/^-?[0-9]+(\.[0-9]{1,4})?$/, 'decimal amount'),
  currency: z.string().regex(/^[A-Z]{3}$/),
});
/** eBay-Site-ID (0 = US, 77 = DE, 3 = UK, ...). */
export const EbaySiteId = z.number().int().min(0).max(999);
/** Legacy-Kennung `u` aus dem Einbettcode (manager_user_liveid), 12 Hexzeichen. */
export const LegacyU = z.string().regex(/^[a-f0-9]{12}$/);
/** New directories use 12 hex characters; older customers also have safe named roots. */
export const HostingDir = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/);
/** Legacy-Galeriekennung `g` aus dem Einbettcode. */
export const LegacyG = DecimalId;
export const Version = z.number().int().nonnegative();
export const Sha256Hex = z.string().regex(/^[a-f0-9]{64}$/);

export type TenantId = z.infer<typeof TenantId>;
export type EbayItemId = z.infer<typeof EbayItemId>;

// ---------------------------------------------------------------------------
// Konto
// ---------------------------------------------------------------------------

export const MigrationState = z.enum([
  'alt', // Konto lebt im Altsystem; Galerie liest MySQL
  'vorbereitet', // Export importiert, noch kein Angebotsimport
  'schatten', // Lesejobs laufen, Schreibpfade gesperrt, Galerie liest noch MySQL
  'wartet-auf-token', // VERALTET seit v0.1: Token-Zustand kommt aus ebayTokenState(); entfällt in v0.2
  'migriert', // Galerie liest neuen Cache, Jobs im Altsystem gestoppt
]);
export type MigrationState = z.infer<typeof MigrationState>;

/** Was der Kunde je Migrationszustand darf (Opus-Anfrage 7). Token-Zustand ist davon getrennt. */
export function migrationCapabilities(state: MigrationState): { login: boolean; view: boolean; editors: boolean; connectEbay: boolean; jobs: boolean } {
  switch (state) {
    case 'alt': return { login: false, view: false, editors: false, connectEbay: false, jobs: false };
    case 'vorbereitet': return { login: true, view: true, editors: false, connectEbay: true, jobs: false };
    case 'schatten': return { login: true, view: true, editors: false, connectEbay: true, jobs: true };
    case 'wartet-auf-token': return { login: true, view: true, editors: false, connectEbay: true, jobs: false };
    case 'migriert': return { login: true, view: true, editors: true, connectEbay: true, jobs: true };
  }
}

/** Kontozustand aus Legacy user_tarif_active: 1 aktiv, 2 gekündigt zum Termin, -1 gesperrt, -2 geschlossen. */
export const AccountStatus = z.enum(['active', 'cancel-scheduled', 'locked', 'closed']);
export type AccountStatus = z.infer<typeof AccountStatus>;
export function legacyTariffToAccountStatus(userTarifActive: number): AccountStatus {
  if (userTarifActive === 1) return 'active';
  if (userTarifActive === 2) return 'cancel-scheduled';
  if (userTarifActive === -1) return 'locked';
  return 'closed';
}

/** Token-Zustand abgeleitet, nicht gespeichert (Opus-Anfrage 7). */
export function ebayTokenState(expiresAt: string | undefined, nowIso: string, warnDays = 30): 'missing' | 'expired' | 'expiring' | 'valid' {
  if (!expiresAt) return 'missing';
  const ms = Date.parse(expiresAt) - Date.parse(nowIso);
  if (ms <= 0) return 'expired';
  return ms < warnDays * 86_400_000 ? 'expiring' : 'valid';
}

export const EbayAuth = z.object({
  type: z.literal('auth-n-auth'),
  /**
   * Name des SSM-SecureString-Parameters mit dem Token, nie der Tokenwert selbst.
   * Konvention: `/pat/<stage>/secret/tenant/<tenantId>/ebay-token`.
   */
  secretRef: z.string().regex(/^\/pat\/(dev|prod)\/secret\/tenant\/[1-9][0-9]{0,19}\/ebay-token$/),
  /**
   * SSM-Parameterversion des gültigen Tokens (Codex-Anfrage 23.10.2026). SSM-Put und
   * DynamoDB-Transaktion sind nicht atomar: beim Neuverbinden erst den alten Token auf
   * seine Version pinnen, neue SSM-Version schreiben, dann Version, Nutzerbindung und
   * META atomar veröffentlichen. Der Worker liest bei gesetzter Version
   * `${secretRef}:${secretVersion}`; fehlt sie, die aktuelle Version. Eine neue,
   * nicht referenzierte SSM-Version ist harmlos.
   */
  secretVersion: z.number().int().positive().optional(),
  /** HardExpirationTime des Tokens. */
  expiresAt: IsoDateTime,
  /** Herkunft des Ablaufdatums: Legacy setzte Import + 470 Tage als Schätzung. Fehlt = 'legacy-estimate'. Ab v0.2 Pflicht. */
  expiresAtSource: z.enum(['legacy-estimate', 'fetch-token', 'token-status']).optional(),
  ebayUserName: z.string().min(1).max(64),
  /** Stabile eBay-Kennung aus GetUser (EIASToken); der Nutzername ist änderbar und nur Anzeige. Beim Import unbekannt. */
  ebayUserId: z.string().min(1).max(128).optional(),
  /** Site, unter der das Konto primär listet (Legacy ebayid_siteid). */
  primarySiteId: EbaySiteId,
  /** Shop-Site für GetStore (Legacy ebayid_store_siteid), unter /account änderbar. */
  storeSiteId: EbaySiteId.optional(),
  /** Shop-URL des Verkäufers (Legacy ebayid_storeurl, aus GetStore); Shopnavigation verlinkt dorthin. */
  storeUrl: z.string().url().max(2048).optional(),
});

export const TenantFeatures = z.object({
  designColors: z.boolean(),
  crossSelling: z.boolean(),
  liveUpdate: z.boolean(),
  /**
   * Veraltet (Flori, 25.10.2026): Das PAT-Logo mit Link entfällt in Galerien und Designs vollständig, weil es kein Signup
   * mehr gibt. Renderer verhalten sich immer wie `logoFree = true`; die App zeigt den Schalter nicht mehr. Der Import darf
   * den Legacy-Rohwert `manager_user_logo_free` weiter ablegen (nur Dokumentation), ab core v0.2 entfällt das Feld.
   */
  logoFree: z.boolean().optional(),
  /**
   * App-BETA (Flori, 28.10.2026): Mandant nutzt die neue App mit eingeschränktem Umfang (Artikel, Designs, Medien,
   * „HTML kopieren“, Galerien lesend, Konto). Aufträge, Überschreiben, Live-Update-Opt-in, Zuordnen und Galerie-
   * Schreibwege antworten serverseitig 403 `beta`. Fehlt = kein BETA-Mandant. Nur Import/Operator setzen es, nie die App.
   */
  appBeta: z.boolean().optional(),
});

export const SyncState = z.object({
  /** Wasserstand für GetSellerEvents (ModTimeFrom), kontoweit. */
  eventsSince: IsoDateTime.optional(),
  /** Optional je Site, falls die Sandbox-Prüfung zeigt, dass der Site-Header filtert. */
  eventsSinceBySite: z.record(z.string().regex(/^[0-9]{1,3}$/), IsoDateTime).optional(),
  /** Sites, die der Reconcile abfragt; leer = primäre Site bzw. alle laut eBay-Antwort. */
  sites: z.array(EbaySiteId).max(30).optional(),
  lastReconcileAt: IsoDateTime.optional(),
  lastReconcileComplete: z.boolean().default(false),
  deltaIntervalMinutes: z.number().int().min(15).max(1440).default(15),
  /**
   * Pause (Festlegung 24.10.2026): der Worker setzt sie atomar (Bedingung: pauseReason fehlt) mit
   * `pausedAt`/`pauseDetail`; der Schedule bleibt bestehen, der Dispatcher verwirft Jobs pausierter
   * Konten mit JobRun-Status 'skipped-paused'. Aufhebung: 'rate-limit' automatisch ab `resumeAfter`;
   * 'token-invalid' nur durch erfolgreiche Neuverbindung (EbayConnectSession), die die Pause löscht
   * und `sync.reconcile` einstellt: reason 'token-renewed' bei identischem eBay-Verkäufer (nicht
   * destruktiv), reason 'user-change' nur bei Verkäuferwechsel (setzt Zuordnungen zurück); 'manual' über die App.
   */
  pauseReason: z.enum(['token-invalid', 'rate-limit', 'manual']).optional(),
  pausedAt: IsoDateTime.optional(),
  pauseDetail: z.string().max(200).optional(),
  resumeAfter: IsoDateTime.optional(),
  /**
   * Monotoner Zähler des Angebotscaches (Codex 23.10.2026): jeder Worker-Schreibzugriff auf
   * Angebote oder Shopkategorien, der keinen Reconcile-/Delta-Wasserstand bewegt (gelesene
   * Beschreibung mit patSearch, store.sync mit itemCount), erhöht ihn atomar am Tenant
   * (CAS). Geht in dataVersionOf ein. Fehlt = 0.
   */
  cacheRevision: z.number().int().nonnegative().optional(),
});

const TenantBase = z.object({
  tenantId: TenantId,
  /** Pflicht für auslieferbare Einbettcodes. Neue Mandanten erhalten eine zufällige 12-Hex-Kennung aus `newLegacyU()`, deren Eindeutigkeit `LEGACY#u/META` per Bedingungsschreiben sichert. */
  legacyU: LegacyU.optional(),
  /**
   * Hostingordner des Mandanten (Legacy design_user_dir, sicherer Pfadteil): Basis-URL für Bilder und
   * Design-Assets ist `https://h.patcdn.net/<hostingDir>/`. Import übernimmt den Altwert; neue
   * Mandanten erhalten eine Zufallskennung wie legacyU (newLegacyU). Fehlt = kein Hostingordner.
   */
  hostingDir: HostingDir.optional(),
  /**
   * Anzeigename nur für Mitglieder des Mandanten. Import (Festlegung 25.10.2026) aus der freigegebenen
   * Profil-Whitelist: `user_firma`, sonst `user_vorname user_name`, sonst `Konto <tenantId>`; kein weiteres Quellfeld.
   */
  displayName: z.string().min(1).max(120),
  /** Fehlt = 'active'. Ab v0.2 Pflicht. */
  accountStatus: AccountStatus.optional(),
  /**
   * Wirksamkeit einer vorgemerkten Kündigung als Zeitpunkt. Legacy `user_last_bill` ist ein Kalendertag
   * (Tarifcron); Import setzt das Ende dieses Tags in Europe/Berlin, siehe `endOfLegacyDay` (exklusive Grenze:
   * 00:00 des Folgetags als UTC-Zeitpunkt). Kein DATE-Vertrag, damit Jobs ohne Zonenwissen vergleichen können.
   */
  cancelEffectiveAt: IsoDateTime.optional(),
  migrationState: MigrationState,
  ebay: EbayAuth.optional(),
  /**
   * Import (Festlegung 25.10.2026, Quellen von Codex belegt): `designColors` = `user_tarif` in {0, 16, 99} (wie die
   * aktive Altfunktion, keine `design_user`-Bedingung); `crossSelling` = eigene `manager_user`-Zeile vorhanden;
   * `liveUpdate` = eigene `manager_user`-Zeile und Tarif nicht in {30, 32, 36} (Recht aus dem Tarif; der Kundenschalter
   * `manager_user_update_active_v3` wird seit 25.10.2026 zu `liveUpdateOptIn.enabled`, siehe dort);
   * `logoFree` = `manager_user_logo_free` = 1 (nur Rohwert, ohne Wirkung, siehe TenantFeatures). Rohwerte bleiben bei
   * `locked`/`closed` erhalten; wirksam sperrt das Kontogate.
   */
  features: TenantFeatures,
  /**
   * Opt-in des Kunden für Live-Update (Flori, 25.10.2026): Nur mit `enabled` darf PAT in die Beschreibungen des
   * eBay-Kontos schreiben; der Kunde schaltet es selbst in der App. Getrennt vom Tarif-Recht `features.liveUpdate`.
   * Import: `manager_user_update_active_v3` = 1 → `enabled`; `changedAt` = `asOf` des Captures (Beobachtungszeit des Imports,
   * keine behauptete historische Kundenänderung), `intervalDays` 30. `intervalDays` = Turnus der automatischen Blockauffrischung
   * (Legacy 30 Tage). Fehlt (Altzeilen) = nicht eingeschaltet.
   */
  liveUpdateOptIn: z.object({ enabled: z.boolean(), changedAt: IsoDateTime, intervalDays: z.number().int().min(1).max(365).default(30) }).optional(),
  sync: SyncState,
  /** Nummernraum für neue Galerien: nächste freie Legacy-`g`. */
  nextGalleryNumber: z.number().int().positive(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export const Tenant = TenantBase;
export type Tenant = z.infer<typeof TenantBase>;

/** Zonenversatz in Minuten zum Zeitpunkt `at` (positiv östlich von UTC). */
function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(at);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? NaN);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/**
 * Ende eines Legacy-Kalendertags (`YYYY-MM-DD`, Geschäftszone Europe/Berlin) als UTC-Zeitpunkt: 00:00 des
 * Folgetags in der Zone, exklusive Grenze. Für `Tenant.cancelEffectiveAt` aus `user_last_bill`.
 */
export function endOfLegacyDay(day: string, timeZone = 'Europe/Berlin'): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) throw new Error('legacy day must be YYYY-MM-DD');
  const nextMidnightUtc = Date.parse(`${day}T00:00:00Z`) + 86_400_000;
  if (!Number.isFinite(nextMidnightUtc)) throw new Error('invalid legacy day');
  let instant = nextMidnightUtc;
  for (let i = 0; i < 2; i++) instant = nextMidnightUtc - zoneOffsetMinutes(new Date(instant), timeZone) * 60_000;
  return new Date(instant).toISOString();
}

/**
 * Datenstand für Vorschau und Worker (Opus-1, Stufe D): letzter vollständiger Abgleich
 * plus Delta-Wasserstand; gleicher Stand ergibt gleiche Auswahl.
 */
export function dataVersionOf(sync: Pick<SyncStateType, 'lastReconcileAt' | 'eventsSince' | 'cacheRevision'>): string {
  const base = `${sync.lastReconcileAt ?? '-'}|${sync.eventsSince ?? '-'}`;
  // Revision 0 bleibt ohne Suffix, damit bestehende Datenstände gleich bleiben.
  return sync.cacheRevision ? `${base}|r${sync.cacheRevision}` : base;
}
type SyncStateType = z.infer<typeof SyncState>;

/** Neue öffentliche Mandantenkennung `u` (12 Hexzeichen, kryptografisch zufällig). */
export function newLegacyU(randomBytes: (n: number) => Uint8Array): string {
  return Array.from(randomBytes(6), b => b.toString(16).padStart(2, '0')).join('');
}
/** secretRef muss den eigenen Mandanten und die Stage tragen; data prüft das vor jedem Schreiben. */
export function tenantSecretRefIsOwn(t: Pick<Tenant, 'tenantId' | 'ebay'>, stage: 'dev' | 'prod'): boolean {
  return !t.ebay || t.ebay.secretRef === `/pat/${stage}/secret/tenant/${t.tenantId}/ebay-token`;
}

export const MemberRole = z.enum(['owner', 'editor', 'viewer']);
export const Member = z.object({
  tenantId: TenantId,
  /** Cognito `sub`. */
  userId: z.string().min(1).max(64),
  role: MemberRole,
  createdAt: IsoDateTime,
});
/**
 * Entfernt-Sperre (Stufe H): nach dem Entfernen eines Mitglieds verhindert diese Zeile, dass der
 * Login-Trigger denselben Cognito-`sub` still erneut mit dem Mandanten verknüpft. Schlüssel
 * keys.memberBlock. Der letzte `owner` kann nicht entfernt oder herabgestuft werden (packages/data).
 * Gleiche E-Mail-Adresse → `/me` zeigt keinen Mandanten mehr; Aufhebung nur durch erneute Einladung.
 */
export const MemberBlock = z.object({
  tenantId: TenantId,
  userId: z.string().min(1).max(64),
  removedAt: IsoDateTime,
  removedBy: z.string().max(64),
});
export type MemberBlock = z.infer<typeof MemberBlock>;

/**
 * Stammdaten des Kontos (Stufe H, 25.10.2026), Schlüssel keys.profile (`TENANT#t/PROFILE`), nur für
 * `owner` lesbar. Import aus `pat_main.user` per Whitelist; die App darf nur den Kontaktteil ändern
 * (`phone`, `contactEmail`, `ccEmail`, `tokenExpiryReminder`), alles andere nur über Import/Nachlauf.
 * Anrede, Newsletter und Sprache entfallen (Flori). `tokenExpiryReminder` beim Import auf true.
 */
export const AccountProfile = z.object({
  tenantId: TenantId,
  legalForm: z.string().max(40).optional(),
  company: z.string().max(120).optional(),
  firstName: z.string().max(80).optional(),
  lastName: z.string().max(80).optional(),
  street: z.string().max(120).optional(),
  zip: z.string().max(16).optional(),
  city: z.string().max(80).optional(),
  country: z.string().length(2).optional(),
  vatId: z.string().max(32).optional(),
  invoiceAddition: z.string().max(255).optional(),
  phone: z.string().max(40).optional(),
  contactEmail: z.string().email().max(254).optional(),
  ccEmail: z.string().email().max(254).optional(),
  payType: z.enum(['direct-debit', 'invoice']).optional(),
  tokenExpiryReminder: z.boolean().default(true),
  source: z.enum(['import', 'app']),
  /** Stand der Altdaten (Exportzeitpunkt des Imports bzw. Nachlaufs); die App ändert ihn nicht. Anzeige „Stand“. */
  asOf: IsoDateTime,
  version: Version,
  /** Letzte Änderung der Zeile (Import, Nachlauf oder Kontaktteil aus der App); `source` nennt die Herkunft des Kontaktteils. */
  updatedAt: IsoDateTime,
});
export type AccountProfile = z.infer<typeof AccountProfile>;
/** Felder, die die App am Profil ändern darf. */
export const appEditableProfileFields = ['phone', 'contactEmail', 'ccEmail', 'tokenExpiryReminder'] as const;

/** Rechnungsstatus im neuen System; `legacyStatus` bleibt roh erhalten, die Abbildung belegt Opus-1 aus value.php. */
export const InvoiceStatus = z.enum(['open', 'reminder', 'dunning', 'paid', 'cancelled', 'direct-debit-pending']);
/**
 * Rechnung (Stufe H), Schlüssel keys.invoice (`TENANT#t/INVOICE#<no>`). Schreiben nur Import und
 * Status-Nachlauf, nie die App. PDF liegt kopiert im privaten Rechnungsspeicher von `prod`
 * (Kopie über den Export-Bucket, Hash im Export); die App signiert kurzlebig (60–300 s) als Download.
 */
export const Invoice = z.object({
  tenantId: TenantId,
  invoiceNo: z.string().min(1).max(40),
  legacyBillId: z.string().max(32).optional(),
  issuedAt: IsoDate,
  periodFrom: IsoDate.optional(),
  periodTo: IsoDate.optional(),
  net: Money.optional(),
  vat: Money.optional(),
  gross: Money.optional(),
  vatRate: z.number().min(0).max(100).optional(),
  payType: z.enum(['direct-debit', 'invoice']).optional(),
  legacyStatus: z.number().int().optional(),
  status: InvoiceStatus,
  paidAt: IsoDate.optional(),
  cancelledAt: IsoDate.optional(),
  /** `versionId` (25.10.2026): konkrete S3-Objektversion im Rechnungsspeicher; der App-Signer signiert sie statt der aktuellen Version, damit spätere Änderungen am Schlüssel den Download nicht verfälschen. Fehlt nur bei Altzeilen vor dem Import-Writer. */
  pdf: z.object({ key: z.string().min(1).max(512), sha256: Sha256Hex, bytes: z.number().int().positive(), versionId: z.string().min(1).max(128).optional() }).optional(),
  source: z.enum(['import', 'followup']),
  asOf: IsoDateTime,
  version: Version,
});
export type Invoice = z.infer<typeof Invoice>;
export type Member = z.infer<typeof Member>;

/**
 * Rückwärtsdatensatz USER#<sub> / MEMBER#<tenantId>, in derselben Transaktion wie
 * Member geschrieben (Opus-Anfrage 1). Damit findet /me die Mandanten ohne Scan.
 */
export const UserMembership = Member;
export type UserMembership = Member;

/** E-Mail-Normalisierung für Legacy-Login und Trigger: trim, lowercase, lockere Form. */
export function normalizeEmail(raw: string): string {
  const email = raw.trim().toLowerCase();
  if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('invalid email');
  return email;
}

/**
 * Importierter Legacy-Login für den Cognito-Migrationstrigger. Nur Hashes, nie Klartext.
 * Mehrere Legacy-Konten je E-Mail mit verschiedenen Passwörtern sind möglich; der
 * Trigger verknüpft nur Konten, deren Hash zum eingegebenen Passwort passt.
 * tenantIds eines Kontos: eigenes Konto plus Gruppenkonten, nur wenn das eigene Konto
 * user_group_switch_off = 0 hat und nur Konten mit Status active/cancel-scheduled.
 */
export const LegacyUserAccount = z.object({
  tenantId: TenantId,
  passwordHash: z.string().startsWith('$2'), // bcrypt
  /**
   * Flori, 23.10.2026: Altverhalten nachbilden. Verglich MySQL mit einer _ci-Kollation,
   * ist der Hash aus dem kleingeschriebenen Passwort gebildet und der Trigger vergleicht
   * ebenfalls kleingeschrieben; sonst exakt. Der Export ermittelt die Kollation.
   */
  passwordHashMode: z.enum(['exact', 'case-insensitive']).default('exact'),
  /** Flori, 23.10.2026: Altpasswort unter 8 Zeichen → Cognito finalUserStatus RESET_REQUIRED, Kunde setzt ein neues. */
  passwordTooShort: z.boolean().default(false),
  language: z.enum(['de', 'en']).default('de'),
  accountStatus: AccountStatus,
  /** Weitere Mandanten aus der Kontogruppe, die dieses Konto erreichen darf. */
  groupTenantIds: z.array(TenantId).default([]),
});
export const LegacyUserV2 = z.object({
  emailLower: z.string().min(3).max(254).refine(v => v === v.trim().toLowerCase(), 'must be normalized'),
  accounts: z.array(LegacyUserAccount).min(1).max(20),
  /**
   * Flori, 23.10.2026: Alle Konten einer Kontogruppe werden zu einem Login zusammengeführt;
   * das erste aktive Konto (kleinste user_id) ist der Login und bestimmt die E-Mail.
   * loginTenantId ist dieses Konto; die übrigen Konten der Gruppe stehen in accounts.
   * Bei „Passwort vergessen“ werden alle Konten dieser E-Mail unter dem neuen Passwort
   * zusammengeführt (alle erhalten MEMBER#).
   */
  loginTenantId: TenantId.optional(),
  locked: z.boolean().default(false),
  /** Gesetzt nach erfolgreicher Übernahme; ein gelöschter Cognito-Nutzer wirkt dann als Sperre. */
  migratedUserId: z.string().optional(),
  migratedAt: IsoDateTime.optional(),
});
export type LegacyUserAccount = z.infer<typeof LegacyUserAccount>;
export type LegacyUserV2 = z.infer<typeof LegacyUserV2>;

/** VERALTET seit v0.1 (ein Hash je E-Mail). Bleibt bis pat-app auf LegacyUserV2 umgestellt ist; entfällt in v0.2. */
export const LegacyUser = z.object({
  emailLower: z.string().email(),
  passwordHash: z.string().startsWith('$2'), // bcrypt
  tenantIds: z.array(TenantId).min(1),
  preferredLanguage: z.enum(['de', 'en']).default('de'),
  migratedUserId: z.string().optional(),
});

/**
 * Wählt den Login einer Kontogruppe: kleinste user_id unter den aktiven Konten
 * (active oder cancel-scheduled); gibt es keines, das kleinste Konto überhaupt.
 */
export function chooseLoginTenant(accounts: ReadonlyArray<{ tenantId: string; accountStatus: AccountStatus }>): string {
  const byId = (a: { tenantId: string }, b: { tenantId: string }) => a.tenantId.length - b.tenantId.length || a.tenantId.localeCompare(b.tenantId);
  const active = accounts.filter(a => a.accountStatus === 'active' || a.accountStatus === 'cancel-scheduled').sort(byId);
  const pick = active[0] ?? [...accounts].sort(byId)[0];
  if (!pick) throw new Error('no accounts');
  return pick.tenantId;
}

/** Übergang: alte Form in die neue überführen (alle Mandanten teilen den einen Hash). */
export function legacyUserToV2(user: z.infer<typeof LegacyUser>, accountStatus: AccountStatus = 'active'): LegacyUserV2 {
  const [first, ...rest] = user.tenantIds;
  return {
    emailLower: normalizeEmail(user.emailLower),
    accounts: [{ tenantId: first!, passwordHash: user.passwordHash, passwordHashMode: 'exact', passwordTooShort: false, language: user.preferredLanguage, accountStatus, groupTenantIds: rest }],
    locked: false,
    ...(user.migratedUserId ? { migratedUserId: user.migratedUserId } : {}),
  };
}
export type LegacyUser = z.infer<typeof LegacyUser>;

// ---------------------------------------------------------------------------
// Angebotscache (Quelle der Wahrheit ist eBay)
// ---------------------------------------------------------------------------

export const ListingType = z.enum(['auction', 'fixed', 'store', 'other']);

export const Item = z.object({
  tenantId: TenantId,
  ebayItemId: EbayItemId,
  site: EbaySiteId,
  title: z.string().max(255),
  listingType: ListingType,
  price: Money,
  pictureUrl: z.string().url().optional(),
  startTime: IsoDateTime,
  endTime: IsoDateTime,
  ended: z.boolean(),
  /** Erste und zweite eBay-Kategorie; Regeln brauchen beide (REGELN_BESTAND.md). */
  categoryId: z.string().max(20).optional(),
  categoryId2: z.string().max(20).optional(),
  /** Erste und zweite Shopkategorie. */
  storeCategoryId: z.string().max(20).optional(),
  storeCategoryId2: z.string().max(20).optional(),
  sku: z.string().max(80).optional(),
  /** eBay Quantity gesamt; mit quantitySold ergibt sich ausverkauft. */
  quantity: z.number().int().nonnegative().optional(),
  quantitySold: z.number().int().nonnegative().optional(),
  /** Abgeleitet vom Sync: lowercase, NFKC, Leerraum normalisiert; für Suche ohne Groß-/Kleinschreibung. */
  titleNorm: z.string().max(255).optional(),
  /**
   * Legacy item_pat_search: Text zwischen `<patsearch>` und `</patsearch>` in der eBay-Beschreibung
   * (Quelle cron include/product.php, historisch 20150309_product.php), Leerraum auf ein Leerzeichen reduziert. Setzt der Worker,
   * wenn er die Beschreibung liest (description.reconcile, Erstimport eingebetteter Angebote), und
   * der Import aus dem Altsystem. Fehlt = unbekannt (nicht „leer“); die Engine meldet 'pat-search'.
   */
  patSearch: z.string().max(2000).optional(),
  hasVariations: z.boolean().default(false),
  /** Für den Galerierenderer (Legacy sgallery): Gebote, Versand, Grundpreis. Fehlt = nicht bekannt, Ausgabe degradiert. */
  bidCount: z.number().int().nonnegative().optional(),
  shipping: z.object({
    free: z.boolean(),
    cost: Money.optional(),
  }).optional(),
  unitPrice: z.object({
    /** Menge als Dezimalstring, z. B. '1.5'. */
    quantity: z.string().regex(/^[0-9]+(\.[0-9]{1,3})?$/),
    /** Einheit laut eBay, z. B. 'kg', 'l', 'm'. */
    unit: z.string().min(1).max(16),
  }).optional(),
  /**
   * Fehlen-Marker der GetItem-Anreicherung (Entscheidung Claude 29.10.2026): gesetzt NUR nach erfolgreichem GetItem ohne
   * UnitInfo; dabei wird ein vorhandenes unitPrice entfernt. Liefert GetItem einen Wert, wird der Marker entfernt.
   * Weder Wert noch Marker = noch nicht angereichert (Kandidat für den begrenzten Durchlauf). Nie Full-Rewrite.
   */
  unitInfoAbsentAt: IsoDateTime.optional(),
  /** Zeitpunkt, zu dem der letzte vollständige Abgleich das Angebot gesehen hat. */
  lastSeenAt: IsoDateTime,
  /** Zuordnung zu einem PAT-Artikel; liegt am Angebot, damit ein Cache-Rebuild sie behält. */
  articleId: z.string().optional(),
});
export type Item = z.infer<typeof Item>;

export function normalizeTitle(title: string): string {
  return title.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Zustand wie im Altsystem: geplant, aktiv, ausverkauft, beendet. */
export function itemStatus(item: Pick<Item, 'startTime' | 'endTime' | 'ended' | 'quantity' | 'quantitySold'>, nowIso: string): 'scheduled' | 'active' | 'soldout' | 'ended' {
  const now = Date.parse(nowIso);
  if (item.ended || Date.parse(item.endTime) <= now) return 'ended';
  if (Date.parse(item.startTime) > now) return 'scheduled';
  if (item.quantity !== undefined && item.quantitySold !== undefined && item.quantity <= item.quantitySold) return 'soldout';
  return 'active';
}

/**
 * Delta-Patch aus GetSellerEvents: nur die Felder, die ein Ereignis liefern kann.
 * Fehlende Felder bleiben erhalten (Repository: UPDATE mit SET nur gesetzter Felder,
 * niemals PUT). articleId ist bewusst ausgeschlossen, es gehört der Domäne Inhalte.
 * Ein Item, das noch nicht im Cache ist, wird vor dem Speichern vollständig gelesen;
 * Platzhalter aus einem Patch sind keine vollständigen Items.
 */
export const ItemDeltaPatch = Item.pick({
  title: true,
  listingType: true,
  price: true,
  endTime: true,
  ended: true,
  quantity: true,
  quantitySold: true,
  titleNorm: true,
  hasVariations: true,
  lastSeenAt: true,
  bidCount: true,
}).partial().extend({ tenantId: TenantId, ebayItemId: EbayItemId, site: EbaySiteId });
export type ItemDeltaPatch = z.infer<typeof ItemDeltaPatch>;

/** Ist-Zustand dessen, was zuletzt in die eBay-Beschreibung geschrieben wurde. */
/** 'diverged': die Beschreibung bei eBay entspricht nicht mehr dem letzten PAT-Stand (manuell geändert oder „ähnlichen Artikel verkaufen“); kein Überschreiben ohne ausdrücklichen Auftrag des Kunden je Angebot (Flori, 24.10.2026). */
export const EmbedState = z.enum(['unknown', 'in-sync', 'pending', 'revising', 'failed', 'blocked', 'diverged']);
/**
 * Backup der vorherigen eBay-Beschreibung (Flori, 25.10.2026): Vor **jedem** Schreibvorgang (Voll-, Block- und
 * Insert-Modus) sichert der Worker die direkt zuvor per GetItem gelesene Rohfassung dauerhaft: Bytes im privaten
 * Backup-Bucket von `apps/sync` (SSE, je Mandant ein Präfix, keine Lebenszyklus-Löschung), Indexzeile unter
 * keys.descriptionBackup. Ohne bestätigtes Backup kein Revise. Zurückspielen ist vorgesehen, aber noch nicht gebaut
 * (Phase 4); bis dahin nur Anlegen und Auflisten. Fingerabdrücke wie Embed.fingerprint.
 */
export const DescriptionBackup = z.object({
  tenantId: TenantId,
  ebayItemId: EbayItemId,
  /** Erfassungszeitpunkt des GetItem, zugleich Sortierschlüssel. */
  capturedAt: IsoDateTime,
  /** SHA-256 der exakten UTF-8-Bytes der gesicherten Beschreibung. */
  sha256: Sha256Hex,
  bytes: z.number().int().nonnegative(),
  /** Objektschlüssel im Backup-Bucket, `descriptions/<tenantId>/<ebayItemId>/<capturedAt>-<sha256>.html`. */
  objectKey: z.string().min(1).max(512),
  /** S3-Objektversion, falls der Bucket versioniert ist. */
  versionId: z.string().min(1).max(128).optional(),
  /** Anlass des folgenden Schreibvorgangs (DescriptionReconcileMessage.payload.reason). */
  reason: z.string().max(32),
  /** Modus des folgenden Schreibvorgangs. */
  mode: z.enum(['full', 'block', 'insert']),
  /** Fingerabdruck der gesicherten Fassung, falls sie ein PAT-Stand war. */
  fingerprintBefore: Sha256Hex.optional(),
  /** Job-/Deliverybezug des Schreibvorgangs, der dieses Backup ausgelöst hat. */
  deliveryId: z.string().max(128).optional(),
});
export type DescriptionBackup = z.infer<typeof DescriptionBackup>;

/**
 * Beleg einer nachträglichen Inspektion (27.10.2026, Pilot D1): Ein Revise endete in `description-write-unresolved`
 * (ACK/Readback verloren); ein späterer, budgetierter Get-only-Vergleich (`runDescriptionOpenWriteInspection`, apps/sync)
 * fand die Live-Beschreibung gleich der beauftragten Fassung. Der dedizierte Data-Operator schließt die offene
 * Schreibreservierung dann als 'written' ab und hängt diesen Beleg an den Ausgang. Gleichheit (actual = desired),
 * Scope und „nur bei written“ prüft der Operator, nicht dieses Schema.
 */
export const DescriptionInspectionEvidence = z.object({
  source: z.literal('inspection'),
  /** Zeitpunkt des gebundenen GetItem der Inspektion. */
  observedAt: IsoDateTime,
  /** Hash der gelesenen Live-Beschreibung (descriptionHtmlHash). */
  actualHtmlHash: Sha256Hex,
  /** Hash der beauftragten Fassung aus der offenen Schreibreservierung (intent.desiredHtmlHash). */
  desiredHtmlHash: Sha256Hex,
  /** Kennung des verbrauchten Inspektions-Once. */
  inspectionOnceId: Sha256Hex,
  /** SHA-256 des kanonischen Inspektionsergebnisses, das der Operator gepinnt hat. */
  inspectionResultSha256: Sha256Hex,
  /** Vor dem Revise gesichertes Backup der offenen Reservierung. */
  backup: DescriptionBackup,
});
export type DescriptionInspectionEvidence = z.infer<typeof DescriptionInspectionEvidence>;

/**
 * Letzter Ausgang eines kundenbeauftragten Beschreibungsauftrags **je Angebot** (25.10.2026, Entscheidung zu Opus' Lücke,
 * Vorschlag Codex-1): eine Zeile je Angebot (keys.descriptionOrderOutcome), vom Worker im terminalen Abschluss atomar mit dem
 * Originalreceipt geschrieben und beim nächsten Auftrag überschrieben; nie app-schreibbar. Unabhängig von EMBED (darf fehlen):
 * ein abgelehnter alter Auftrag sagt nichts über den aktuellen Beschreibungsstand. Die App zeigt daraus den Hinweis je Angebot
 * („bitte neu beauftragen“ bei 'article-changed').
 */
export const DescriptionOrderOutcome = z.object({
  tenantId: TenantId,
  ebayItemId: EbayItemId,
  /** Job, der den terminalen Ausgang erzeugt hat: das description.revise-Child oder, bei frühem Abschluss vor dem Child, der ursprüngliche description.reconcile-Job (jeweils mit seinem Originalreceipt). */
  jobId: z.string().min(1).max(128),
  articleId: z.string().min(1),
  /** Beauftragte veröffentlichte Fassung; 0 ist gültig. */
  articleVersion: Version,
  /** 'written' = tatsächlicher Revise mit Backup; 'already-current' = bestätigter No-op (Live-HTML war bereits die beauftragte Fassung, kein Revise/Backup); 'article-changed' / 'source-missing' = terminal abgelehnt. */
  outcome: z.enum(['written', 'already-current', 'article-changed', 'source-missing']),
  /** Abschlusszeit des terminalen Schritts. */
  at: IsoDateTime,
  /** Beleg des Schreibvorgangs bei 'written' (DescriptionBackup.deliveryId), sonst leer. */
  deliveryId: z.string().max(128).optional(),
  /** Nur bei 'written' durch nachträgliche Inspektion (statt ACK): Beleg des Get-only-Vergleichs. Normale Ausgänge haben das Feld nicht. */
  inspection: DescriptionInspectionEvidence.optional(),
});
export type DescriptionOrderOutcome = z.infer<typeof DescriptionOrderOutcome>;

/**
 * Regulärer Klärungsweg für `description-write-unresolved` (Entscheidung Claude 28.10.2026 nach Codex-1
 * `apps/sync/docs/DESCRIPTION_UNRESOLVED_CORE_VORSCHLAG.md`): Ein Revise, dessen ACK/Readback verloren ging, bekommt
 * einen dauerhaften Klärungsfall (keys.descriptionResolution). Der Fall wird im Original-Write-Reservierungs-TX absent-only
 * angelegt, per Kontrollnachricht `description.resolve` (eigene Control-FIFO) geweckt, höchstens drei Mal per gebundenem
 * Get-only-Vergleich inspiziert (Generationen 01..03, je eigene INSPECT-Once und Beobachtung) und bei `desired` genau
 * einmal adoptiert (ADOPT-Once). Alle Sätze: schemaVersion 1, kein TTL, nie app-schreibbar; Zustandsnamen erzeugen keine
 * Rechte, maßgeblich bleiben Once/Lease/CAS. Evidence gilt 24 h; Beruhigung 15 min nach `invocationNotAfter` des Originals.
 */
export const DescriptionResolutionState = z.enum(['reserved', 'waiting-original', 'waiting-budget', 'inspect-ready', 'inspecting',
  'observed', 'adopt-ready', 'adopting', 'manual', 'closed']);
export type DescriptionResolutionState = z.infer<typeof DescriptionResolutionState>;
export const DescriptionResolutionReason = z.enum(['inspection-unknown', 'evidence-unknown', 'observation-backup', 'observation-different',
  'evidence-expired', 'authority-changed', 'adoption-unknown', 'generations-exhausted', 'budget-wait']);
export type DescriptionResolutionReason = z.infer<typeof DescriptionResolutionReason>;
/** Unveränderliche Identitätsbasis; die kanonischen Bytes liefert descriptionResolutionCanonicalV1, den SHA-256 rechnet Data/Sync. */
export const DescriptionResolutionIdentity = z.object({
  stage: z.enum(['dev', 'prod']),
  account: z.string().regex(/^[0-9]{12}$/),
  region: z.string().min(1).max(32),
  tableArn: z.string().min(1).max(512),
  tenantId: TenantId,
  ebayItemId: EbayItemId,
  originalChildJobId: z.string().min(8).max(128),
});
export type DescriptionResolutionIdentity = z.infer<typeof DescriptionResolutionIdentity>;
/** Kanonisches v1-Layout: JSON mit sortierten Schlüsseln, ohne Whitespace, UTF-8; `domain` fest. */
export function descriptionResolutionCanonicalV1(input: DescriptionResolutionIdentity): string {
  const checked = DescriptionResolutionIdentity.parse(input);
  const entries = Object.entries({ domain: 'pat.description.resolve/v1', ...checked }).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify(Object.fromEntries(entries));
}
const BackupRef = z.object({ objectKey: z.string().min(1).max(512), versionId: z.string().min(1).max(128).optional(), sha256: Sha256Hex, capturedAt: IsoDateTime });
const ResolutionEntry = z.object({
  /** Einmalige Versuchs-ID; ein späterer Worker darf sie weder neu erzeugen noch nachladen. */
  attemptId: Sha256Hex,
  generation: z.number().int().min(0).max(3),
  scopeHash: Sha256Hex,
  /** Exakter geplanter TX-Hash vor dem möglicherweise unbekannten Budget-/Completionversand. */
  plannedTxHash: Sha256Hex,
  at: IsoDateTime,
});
export const DescriptionResolution = z.object({
  tenantId: TenantId,
  ebayItemId: EbayItemId,
  schemaVersion: z.literal(1),
  resolutionId: Sha256Hex,
  identity: DescriptionResolutionIdentity,
  createdAt: IsoDateTime,
  originalIntentHash: Sha256Hex,
  originalMessageHash: Sha256Hex,
  originalAuthorityHash: Sha256Hex,
  /** Dauerhafte Herkunft aus echten Reads des Originalproducers; überlebt die Retention der Transportrows. */
  provenance: z.object({
    rootKey: z.object({ PK: z.string().min(1), SK: z.string().min(1) }),
    parentJobId: z.string().min(8).max(128),
    rootCutoff: IsoDateTime,
    secretVersion: z.number().int().positive(),
    site: EbaySiteId,
    sellerId: z.string().min(1).max(128).optional(),
  }),
  backup: BackupRef,
  /** Grenzen des ursprünglichen Versuchs aus dem echten Scope; keine Callerzeit. */
  originalAttempt: z.object({
    ownerHash: Sha256Hex,
    fence: z.number().int().positive(),
    leaseExpiresAtMs: z.number().int().positive(),
    invocationNotAfter: IsoDateTime,
    dispatchNotAfter: IsoDateTime,
    reservedAt: IsoDateTime,
  }),
  revision: z.number().int().nonnegative(),
  state: DescriptionResolutionState,
  reason: DescriptionResolutionReason.optional(),
  nextAttemptAt: IsoDateTime.optional(),
  wakeRevision: z.number().int().nonnegative(),
  /** 0..3; wird nur in derselben bedingten TX wie die Anlage der INSPECT-Once erhöht. */
  inspectionGeneration: z.number().int().min(0).max(3),
  inspectionEntry: ResolutionEntry.optional(),
  adoptionEntry: ResolutionEntry.optional(),
  /** Dauerhafter historischer Abschluss; nie aus aktuellem Itemzustand rekonstruiert. */
  closed: z.object({
    closedAt: IsoDateTime,
    closedBy: z.enum(['original', 'inspection']),
    originalReceiptJobId: z.string().min(8).max(128),
    resultHash: Sha256Hex,
    writePlanHash: Sha256Hex,
    inspection: DescriptionInspectionEvidence.optional(),
  }).optional(),
  updatedAt: IsoDateTime,
});
export type DescriptionResolution = z.infer<typeof DescriptionResolution>;
/** Verbrauchtes Ausführungsrecht; Existenz = verbraucht, niemals nachladbares Permit. INSPECT je Generation 1..3, ADOPT einmal (generation 0). */
export const DescriptionResolutionOnce = z.object({
  tenantId: TenantId,
  resolutionId: Sha256Hex,
  schemaVersion: z.literal(1),
  phase: z.enum(['inspect', 'adopt']),
  generation: z.number().int().min(0).max(3),
  attemptId: Sha256Hex,
  originalIntentHash: Sha256Hex,
  ownerHash: Sha256Hex,
  fence: z.number().int().positive(),
  invocationNotAfter: IsoDateTime,
  inputHash: Sha256Hex,
  budgetHash: Sha256Hex.optional(),
  planHash: Sha256Hex,
  createdAt: IsoDateTime,
}).refine(v => (v.phase === 'adopt' ? v.generation === 0 : v.generation >= 1), { message: 'inspect braucht generation 1..3, adopt generation 0' });
export type DescriptionResolutionOnce = z.infer<typeof DescriptionResolutionOnce>;
/** Genau ein immutables Producerergebnis je Inspektionsgeneration; `desired`, `backup`, `different` sind alle speicherbar. */
export const DescriptionResolutionObservation = z.object({
  tenantId: TenantId,
  resolutionId: Sha256Hex,
  schemaVersion: z.literal(1),
  generation: z.number().int().min(1).max(3),
  result: z.enum(['desired', 'backup', 'different']),
  observedAt: IsoDateTime,
  actualHtmlHash: Sha256Hex,
  /** Bytes des tatsächlich gelesenen HTML; 0 ist erlaubt (eBay liefert bei vorhandenem leerem Description ''). */
  bytes: z.number().int().nonnegative(),
  desiredHtmlHash: Sha256Hex,
  backupHtmlHash: Sha256Hex,
  backup: BackupRef,
  inspectionOnceId: Sha256Hex,
  originalMessageHash: Sha256Hex,
  originalIntentHash: Sha256Hex,
  producer: z.string().min(1).max(64),
  /** SHA-256 der exakt kanonischen Ergebnisbytes des Producers. */
  canonicalSha256: Sha256Hex,
});
export type DescriptionResolutionObservation = z.infer<typeof DescriptionResolutionObservation>;
/** Dauerhafter Kontroll-Wake (keys.descriptionResolutionWake); erzeugt oder resettet keine Once. */
export const DescriptionResolutionWake = z.object({
  tenantId: TenantId,
  resolutionId: Sha256Hex,
  schemaVersion: z.literal(1),
  dueAt: IsoDateTime,
  wakeRevision: z.number().int().nonnegative(),
  caseRevision: z.number().int().nonnegative(),
  target: DescriptionResolutionState,
  createdAt: IsoDateTime,
});
export type DescriptionResolutionWake = z.infer<typeof DescriptionResolutionWake>;

/**
 * Globaler Discovery-Cursor des Control-Sweeps (eine Zeile, dev wie prod): faire Rotation über die Kandidatenmandanten
 * und deren fällige Wakes, Seitengröße 25. Fortschritt nur nach tatsächlich gelesener Seite; bei Send-unknown/busy bleibt der
 * Cursor stehen (Seite wird in der nächsten Runde erneut gelesen). Nach dem letzten Mandanten beginnt eine neue Runde.
 * Keine zusätzliche Globalrow je Mandant, kein GSI, kein Scan.
 */
export const DescriptionResolutionSweepCursor = z.object({
  schemaVersion: z.literal(1),
  round: z.number().int().nonnegative(),
  /** Index in der festen, sortierten Kandidatenliste (dev: die beiden Demomandanten). */
  tenantIndex: z.number().int().nonnegative(),
  tenantId: TenantId,
  /** Letzter vollständig verarbeiteter Wake-SK dieses Mandanten in dieser Runde; fehlt am Mandantenanfang. */
  lastWakeSk: z.string().min(1).max(256).optional(),
  pageSize: z.literal(25),
  updatedAt: IsoDateTime,
  updatedBy: z.string().min(1).max(128),
});
export type DescriptionResolutionSweepCursor = z.infer<typeof DescriptionResolutionSweepCursor>;

export const Embed = z.object({
  tenantId: TenantId,
  ebayItemId: EbayItemId,
  /** Import (24.10.2026): Angebot mit Artikelbezug → 'full' (Artikel ersetzt die ganze Beschreibung); Blockmodus-Angebot ohne Artikel (item_live_update_xs=1) → 'block'. Fehlt nur bei unbekanntem Altzustand; der Worker setzt den Modus dann beim ersten Abgleich. */
  mode: z.enum(['full', 'block']).optional(),
  /** Fehlt = 'unknown'. Ab v0.2 Pflicht. */
  state: EmbedState.optional(),
  stateSince: IsoDateTime.optional(),
  /**
   * Fehlt = 'worker'. Ab v0.2 Pflicht. 'adopted' (Festlegung 25.10.2026, Demo 1): ein eigenes Angebot wurde in der App
   * einem Artikel zugeordnet (Item.articleId gesetzt, Outbox `description.reconcile` mit reason 'assigned'); der erste
   * Lauf liest die aktuelle Beschreibung nur, setzt `fingerprint` mit rendererVersion 'adopted-baseline' und
   * `galleries` aus den gefundenen Markern (kein Schreiben). Danach gilt: mode 'full' darf beim nächsten Lauf
   * revidieren, weil die Zuordnung der ausdrückliche Auftrag des Kunden ist; mode 'block' ersetzt nur bei vorhandenem
   * Marker, sonst state 'blocked' mit Grund `no-marker`. Der Legacy-Baselinepfad (schatten, legacy-import) bleibt getrennt.
   * D5 (neues eigenes Angebot ohne Zuordnung, Legacy-Verhalten item_live_update_xs): Autorität nur, wenn das Angebot über
   * das eigene Token des Mandanten synchronisiert wurde, der Marker `u`/`g` serverseitig auf eine eigene Galerie dieses
   * Mandanten auflöst, `features.liveUpdate` gilt und der Mandant `migriert` ist; dann reason 'marker-detected', mode 'block',
   * origin 'adopted', erster Lauf nur lesen. Ein Marker allein ohne diese Prüfungen ist keine Schreibautorität.
   */
  origin: z.enum(['legacy-import', 'worker', 'adopted']).optional(),
  articleId: z.string().optional(),
  articleVersion: Version.optional(),
  designId: z.string().optional(),
  designVersion: Version.optional(),
  /**
   * Ist-Zustand je Galerie: Hash der eingebetteten geordneten Zielliste; null = unbekannt (Import).
   * `orderedItemIds` ist die eingebettete Liste selbst (Worker nach jedem Revise; Import aus dem
   * Altsystem, soweit vorhanden). Daraus zählt description.reconcile die beendeten alten Ziele für
   * die 2/4/6-Schwellen. Fehlt die Liste, gilt die Schwelle als erreicht: einmal budgetiert
   * revidieren, danach ist sie bekannt.
   */
  galleries: z.record(z.string(), z.object({
    selectionHash: Sha256Hex.nullable(),
    orderedItemIds: z.array(EbayItemId).max(100).optional(),
  })),
  /** Aus dem Import: Legacy item_live_update_xs = Beschreibung enthält `x.patcdn.net/sgallery.php`, in beiden Modi (Opus-2, 23.10.2026). */
  legacyHasGallery: z.boolean().optional(),
  revisedAt: IsoDateTime.optional(),
  /**
   * Ist-Fingerabdruck der zuletzt gesendeten Beschreibung (Festlegung 24.10.2026):
   * fingerprint = SHA256(UTF8(JSON.stringify(['pat-description-v1', rendererVersion, htmlHash])))
   * mit htmlHash = SHA256 der exakten UTF-8-Bytes des gesendeten HTML. Bindet Inhalt und beide
   * Renderer; `htmlHash` selbst liegt im technischen Begleitsatz von packages/data.
   * Nach dem Import gibt es keinen verlässlichen letzten PAT-Stand (Codex-Prüfung 24.10.2026):
   * der erste Lauf liest die aktuelle Beschreibung nur und setzt daraus eine Basis mit
   * rendererVersion 'legacy-baseline' (kein Schreiben). Änderungen vor der Migration sind damit
   * nicht erkennbar, alle späteren schon.
   */
  fingerprint: Sha256Hex.optional(),
  /** Zusammengesetzte Renderer-Version, mit der `fingerprint` erzeugt wurde: `<design-html>+gallery-<gallery>`, z. B. `1.1.1+gallery-1.0.0`. */
  rendererVersion: z.string().max(64).optional(),
  /** Letzter Fehler des tatsächlichen Beschreibungsstands (Renderer/Schreibvorgang). Ausgänge kundenbeauftragter Aufträge stehen **nicht** hier, sondern in DescriptionOrderOutcome (25.10.2026). */
  lastError: z.object({ at: IsoDateTime, code: z.string().max(64), message: z.string().max(500) }).optional(),
  /** Gesetzt, wenn der Vorabvergleich (GetItem-HTML gegen `fingerprint`) eine fremde Änderung zeigt; `divergedHtmlHash` ist der Hash des vorgefundenen HTML. */
  divergedAt: IsoDateTime.optional(),
  divergedHtmlHash: Sha256Hex.optional(),
  /** Nicht fatale Renderer-Hinweise der letzten Revision (design-html WarningCode mit Schwere 'warning'); fatale Codes landen in lastError. */
  warnings: z.array(z.string().max(64)).max(20).optional(),
  /** Zähler gilt für den UTC-Tag `revisionsDay` (YYYY-MM-DD); anderer Tag = Zähler beginnt bei 0. */
  revisionsToday: z.number().int().nonnegative().default(0),
  revisionsDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type Embed = z.infer<typeof Embed>;

/**
 * Soll-Zustand: berechnete Zielangebote einer Galerie für ein Ausgangsangebot.
 * Wird nur für Ausgangsangebote berechnet, die eingebettet sind (Embed existiert
 * oder Artikel zugeordnet), nicht für den gesamten Bestand.
 */
export const Selection = z.object({
  tenantId: TenantId,
  sourceItemId: EbayItemId,
  galleryId: z.string(),
  site: EbaySiteId,
  orderedItemIds: z.array(EbayItemId).max(100),
  selectionHash: Sha256Hex,
  rulesVersion: Version,
  dataVersion: z.string().max(64),
  engineVersion: z.string().max(16),
  computedAt: IsoDateTime,
  usedFallback: z.boolean(),
  /** = MatchResult.selectionComplete. Nur true-Auswahlen darf description.revise einbetten. */
  complete: z.boolean(),
  /** Galerieversion zum Zeitpunkt der Berechnung (Einbettcode/Einstellungen); Teil des Snapshot-Vergleichs vor dem Revise. Fehlt = unbekannt, ab v0.2 Pflicht. */
  galleryVersion: Version.optional(),
});
export type Selection = z.infer<typeof Selection>;

// ---------------------------------------------------------------------------
// Cross-Selling
// ---------------------------------------------------------------------------

/** Einstellungen aus dem Altsystem (manager_gallery_setting), vereinheitlicht. */
const Hex6 = z.string().regex(/^[0-9a-fA-F]{6}$/);
/**
 * Einstellungen für einen zum Altsystem gleichen Einbettcode (Opus-1, Stufe D, 23.10.2026).
 * Zuordnung zu sgallery-Parametern: c1/c3/i1/i2/i3 Farben (c2 wird abgeleitet), w Breite,
 * o1 titleLines, o9 Großbild, o11 Zeilenhöhe, la Sprache, t = galleryType (fest 0 im
 * aktuellen Vertrag). randomize ist mit sortMode 1..3 nicht kombinierbar.
 */
export const GallerySettings = z.object({
  galleryType: z.literal(0).default(0),
  insertMode: z.enum(['none', 'top', 'bottom']).default('none'), // Legacy update_insert_* (Einfügen oben/unten), nicht automode
  maxItems: z.number().int().min(1).max(100),
  /** Zeilen 1..9 (Legacy o11). Fehlt = 2. Bis v0.2 optional. */
  height: z.number().int().min(1).max(9).optional(),
  width: z.number().int().min(100).max(2000).optional(),
  /** Legacy o1: 0 = ohne Titel. */
  titleLines: z.number().int().min(0).max(4).default(3),
  showPrice: z.boolean().default(true),
  showInfoText: z.boolean().default(true),
  showAuctionType: z.boolean().default(true),
  /** Legacy o9. Fehlt = false. Bis v0.2 optional. */
  bigPicture: z.boolean().optional(),
  pictureSize: z.number().int().positive().optional(),
  fontSizeButton: z.number().int().positive().optional(),
  fontStyleButtonBold: z.boolean().default(false),
  linkTarget: z.enum(['_blank', '_self']).default('_blank'),
  drawStyle: z.number().int().nonnegative().default(0),
  groupNoByTitle: z.boolean().default(false),
  language: z.enum(['de', 'en', 'fr', 'it']).default('de'),
  align: z.enum(['left', 'center', 'right']).default('center'),
  colors: z.object({ c1: Hex6, c3: Hex6, i1: Hex6, i2: Hex6, i3: Hex6 }).optional(),
  /** Nur Angebote derselben Site wie das Ausgangsangebot. Fehlt = true. Bis v0.2 optional. */
  sameSiteOnly: z.boolean().optional(),
  /** Sortierung: 0/4/5 keine, 1 Startzeit absteigend, 2 Preis aufsteigend, 3 Preis absteigend. */
  sortMode: z.number().int().min(0).max(5).default(0),
  /** randomize mit sortMode 1..3 lehnt die Regel-Engine ab (`unsupported-sort`); core prüft das nicht doppelt. */
  randomize: z.boolean().default(false),
});

/**
 * Kontrollsatz je Mandant für den sgallery-Piloten (Entscheidung Claude 28.10.2026 nach Entwurf Codex-1,
 * `apps/sync/docs/GALLERY_READER_PATDATA_ENTWURF.md` §5): Der GalleryReader liest ihn vor jeder Quellenwahl
 * (keys.sgalleryControl). Fehlender Satz bei gültiger Zuordnung bedeutet `legacy`; ungültiger Satz oder Lesefehler
 * ist ein geschlossener technischer Fehler, nie `legacy`. Rückweg ist ausschließlich ein bedingtes Umschalten auf
 * `legacy` mit neuer `revision` (CAS), kein Datenrückspiel. Nie app-schreibbar; nur der Betreiber-Operator schreibt.
 */
export const SgalleryControl = z.object({
  tenantId: TenantId,
  schemaVersion: z.literal(1),
  /** Quelle der öffentlichen sgallery-Antwort für diesen Mandanten. */
  source: z.enum(['legacy', 'new']),
  /** Monoton steigend; jede Änderung schreibt revision + 1 bedingt auf die gelesene revision. */
  revision: z.number().int().nonnegative(),
  /** Shadow-Vergleich je Request gegen die jeweils andere Quelle (Abschnitt 6 des Entwurfs). */
  comparison: z.enum(['off', 'every-request']),
  /** Freigegebener Daten-/Projektionsstand für `new`; ohne passende Readiness bleibt `new` geschlossen. */
  readinessRevision: z.number().int().nonnegative().optional(),
  updatedAt: IsoDateTime,
  /** Kennung des Operatorlaufs (kein Personenbezug). */
  updatedBy: z.string().min(1).max(128),
});
export type SgalleryControl = z.infer<typeof SgalleryControl>;

/**
 * eBay-Lesebudget des prod-Readsyncs (Entscheidung Claude 28.10.2026): Das einzige prod-Keyset teilen sich Legacy-Live-Update
 * und der neue Sync; eBay begrenzt die App auf 5.000 Trading-Calls je Tag. Pilotgrenzen: 600 Calls/Tag je Mandant (bis 29.10.2026: 300)
 * (keys.ebayReadBudget), 1.500/Tag gesamt (keys.ebayReadBudgetGlobal). Jeder Call reserviert vorher bedingt (CAS) einen Zähler;
 * bei Erreichen stoppt der Sync bis zum nächsten UTC-Tag, nichts wird erstattet. Fremde Keyset-Nutzer werden nicht mitgezählt.
 */
export const EbayReadBudget = z.object({
  schemaVersion: z.literal(1),
  /** UTC-Tag `YYYY-MM-DD`. */
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Mandant oder 'global'. */
  scope: z.union([TenantId, z.literal('global')]),
  calls: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  updatedAt: IsoDateTime,
});
export type EbayReadBudget = z.infer<typeof EbayReadBudget>;
/**
 * perTenantUnitInfoPerDay: Zweckzähler der GetItem-UnitInfo-Anreicherung (keys.ebayReadUnitInfoBudget), 100 Calls/Tag je Mandant,
 * in EINER Transaktion zusammen mit Mandanten- (300) und Gesamtbudget (1.500) reserviert; Erschöpfung pausiert nur die Anreicherung.
 */
/** perTenantPerDay 300→600 (Claude 29.10.2026): nach der 10007-Mitigation laufen große Pilot-Reconciles mit 50er-Seiten und brauchen mehr Calls je Tag; globalPerDay bleibt die harte Grenze. */
export const EBAY_READ_BUDGET_PILOT = Object.freeze({ perTenantPerDay: 600, globalPerDay: 1500, perTenantUnitInfoPerDay: 100 });

export const Gallery = z.object({
  tenantId: TenantId,
  /**
   * Festlegung 24.10.2026: `galleryId` ist die Zeichenkette der Legacy-`manager_gallery_id` bzw. der
   * vom Zähler vergebenen neuen `g`, also immer gleich `legacyG`. Designboxen und Einbettcodes
   * verweisen darauf; der Import ändert keine Galerie-IDs.
   */
  galleryId: z.string(),
  legacyG: LegacyG,
  /** Legacy-Quellkonto (Mehrkonten-Mandant); fehlt = Primärquelle Tenant.legacyU. */
  legacyU: LegacyU.optional(),
  site: EbaySiteId,
  name: z.string().min(1).max(120),
  settings: GallerySettings,
  version: Version,
  deleted: z.boolean().default(false),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Gallery = z.infer<typeof Gallery>;

// ---------------------------------------------------------------------------
// Inhalte
// ---------------------------------------------------------------------------

export const BlockType = z.enum(['header', 'text', 'picture', 'html', 'video']);
const BlockId = z.string().min(1).max(40);
/**
 * Blockdaten je Typ (Opus-2, Legacy-Abgleich 23.10.2026, packages/design-html/docs/LEGACY_MAP.md §4).
 * `picture.list[]`: entweder `mediaId` (Medienverzeichnis) oder `url` (absolut, https).
 * `video` gibt es im Altsystem nicht; Entscheidung Claude: verlinktes Vorschaubild auf
 * eine https-URL (YouTube/Vimeo), nie iframe, weil eBay keine aktiven Inhalte erlaubt.
 */
export const HeaderBlockData = z.object({ text: z.string().max(500) });
export const TextBlockData = z.object({
  text: z.string().max(400 * 1024),
  text2: z.string().max(400 * 1024).optional(),
  options: z.object({ columnCount: z.union([z.literal(1), z.literal(2)]).optional() }).optional(),
});
export const HtmlBlockData = z.object({ html: z.string().max(200_000) });
export const PictureBlockData = z.object({
  list: z.array(z.object({
    mediaId: z.string().max(64).optional(),
    /** https-URL, oder übergangsweise eine Data-URL (data:image/…) aus dem Altsystem bis zu 2 MB; der Import soll Data-URLs in Medien umwandeln (Opus-1). */
    url: z.string().max(2_000_000).refine(u => /^https:\/\//.test(u) || /^data:image\/(png|jpeg|gif|webp);base64,/.test(u), 'https oder data:image').optional(),
    label: z.string().max(400 * 1024).optional(),
  }).refine(p => Boolean(p.mediaId) !== Boolean(p.url), 'mediaId oder url, nicht beides')),
  options: z.object({ format: z.enum(['list', 'prevtop', 'prevbottom']).optional() }).optional(),
});
export const VideoBlockData = z.object({
  url: z.string().url().max(2048).refine(u => u.startsWith('https://'), 'https only'),
  thumbnailMediaId: z.string().max(64).optional(),
});
export const Block = z.discriminatedUnion('type', [
  z.object({ id: BlockId, type: z.literal('header'), data: HeaderBlockData }),
  z.object({ id: BlockId, type: z.literal('text'), data: TextBlockData }),
  z.object({ id: BlockId, type: z.literal('picture'), data: PictureBlockData }),
  z.object({ id: BlockId, type: z.literal('html'), data: HtmlBlockData }),
  z.object({ id: BlockId, type: z.literal('video'), data: VideoBlockData }),
]);
export type Block = z.infer<typeof Block>;

/**
 * Vorschaubild der Artikelliste (Kundenwunsch 03.11.2026, wie Legacy `artikel_first_picture`): `m:<mediaId>` oder eine
 * https-Adresse. Abgeleitet beim Speichern, nie vom Client gesetzt; Data-URLs zählen nicht (zu groß für den Kopf).
 */
export const ArticlePreviewPicture = z.string().max(2048).refine(v => /^m:[A-Za-z0-9_-]{1,64}$/.test(v) || /^https:\/\/[^\s]+$/.test(v));
/**
 * Legacy-Regel (app3 designitem `setFirstPicture`): erstes Bild der Bildblöcke in Blockreihenfolge. Abweichung: Bilder,
 * die keine gültige Vorschau ergeben (Data-URL, zu lang, ungültige Medien-ID), werden übersprungen.
 */
export function articlePreviewPicture(blocks: readonly Block[]): string | undefined {
  for (const block of blocks) {
    if (block.type !== 'picture') continue;
    for (const picture of block.data.list) {
      const value = picture.mediaId !== undefined ? `m:${picture.mediaId}` : picture.url;
      if (value !== undefined && ArticlePreviewPicture.safeParse(value).success) return value;
    }
  }
  return undefined;
}

export const Article = z.object({
  tenantId: TenantId,
  articleId: z.string(),
  title: z.string().min(1).max(255),
  sku: z.string().max(80).optional(),
  /** Verweis auf ArticleGroup.groupId (Legacy artikel_gruppen); fehlt = ohne Gruppe. */
  groupId: z.string().optional(),
  designId: z.string().optional(),
  firstPictureMediaId: z.string().optional(),
  /** `articlePreviewPicture(blocks)` zum Zeitpunkt des Speicherns; fehlt = kein Bild oder Kopf noch ohne Nachtrag. */
  previewPicture: ArticlePreviewPicture.optional(),
  blocks: z.array(Block).max(200),
  /** Verweis auf S3, falls die Blöcke die Datensatzgrenze überschreiten. */
  blocksObjectKey: z.string().optional(),
  /** Herkunft aus dem Altsystem: 2 = konvertierter App2-Artikel, 3 = App3; fehlt bei neu angelegten Artikeln (Flori 29.10.2026). */
  legacyAppVersion: z.union([z.literal(2), z.literal(3)]).optional(),
  version: Version,
  publishedVersion: Version.optional(),
  deleted: z.boolean().default(false),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Article = z.infer<typeof Article>;

/** Boxtypen laut Legacy design_content_type 0/1/2/9 (Opus-2, LEGACY_MAP §3). */
export const DesignBoxType = z.enum(['text', 'gallery', 'shop-navi', 'article']);
export const DesignBox = z.object({
  id: z.string().min(1).max(40),
  type: DesignBoxType,
  headline: z.string().max(255).optional(),
  /** Legacy-TEXT (65.535 Byte); Designs haben keine Auslagerung. */
  text: z.string().max(65_535).optional(),
  icon: z.string().max(100).optional(),
  /** Legacy: 1 links, 2 Hauptspalte, 3 Fuß. 0 rendert das Altsystem nie. */
  column: z.number().int().min(1).max(3).default(2),
  order: z.number().int().nonnegative(),
  /** Für type 'gallery': Galerie dieser Box. */
  galleryId: z.string().optional(),
  /** Numerische Legacy design_content_id; erscheint in Element-IDs, Kunden-CSS referenziert sie. */
  legacyId: z.string().regex(/^[1-9][0-9]{0,19}$/).optional(),
  /** Für type 'shop-navi' (Legacy design_shop_navi_*). Filter 'all-with-other' nur aus Altdaten. */
  shopNavi: z.object({
    active: z.boolean().optional(),
    order: z.enum(['ebay', 'name']).default('ebay'),
    filter: z.enum(['all', 'with-items', 'all-with-other']).default('with-items'),
    depth: z.union([z.literal(0), z.literal(1), z.literal(2)]).default(0),
  }).optional(),
});
/** URL im Altsystem frei getippt (auch leer oder relativ); Prüfung und https-Umschreibung macht der Renderer. */
export const DesignLink = z.object({ id: z.string(), title: z.string().max(255), url: z.string().max(2048), icon: z.string().max(100).optional(), order: z.number().int().nonnegative() });
export const DesignTrust = z.object({ id: z.string(), title: z.string().max(255), icon: z.string().max(100).optional(), order: z.number().int().nonnegative() });
/**
 * Farb- und Schriftschlüssel des Altsystems (design_color_setting::checkData, Opus-2
 * LEGACY_MAP §2). Hintergründe und Schriftfarben sind Objekte; Zahlen kommen im
 * Altbestand auch als Strings vor. Altschlüssel `header(_bg)`, `col_lr(_bg)`,
 * `col_main(_bg)` bildet der Import nach der Legacy-Regel auf `color_1..3` ab.
 */
const NumOrStr = z.union([z.number().int(), z.string().max(8)]);
const HexLoose = z.union([z.string().regex(/^[0-9a-fA-F]{1,6}$/), z.number().int().nonnegative()]);
export const LegacyBackground = z.object({
  bg_type: NumOrStr.optional(),
  color1: HexLoose.optional(),
  color2: HexLoose.optional(),
  overlay: z.union([z.string().max(2048), z.number(), z.null()]).optional(),
  bgimage: z.string().max(2048).optional(),
  repeat: z.string().max(32).optional(),
  position: z.string().max(64).optional(),
  size: z.string().max(64).optional(),
  gradient: NumOrStr.optional(),
  direction: z.string().max(32).optional(),
  shadow_color: HexLoose.optional(),
}).passthrough();
export const LegacyFontColor = z.object({
  active: NumOrStr.optional(),
  font_color: HexLoose.optional(),
}).passthrough();
export const DesignColors = z.object({
  font_family: z.string().max(200).optional(),
  font_size: NumOrStr.optional(),
  body_bg: LegacyBackground.optional(),
  body_opacity: NumOrStr.optional(),
  color_1: LegacyFontColor.optional(),
  color_1_bg: LegacyBackground.optional(),
  color_2: LegacyFontColor.optional(),
  color_2_bg: LegacyBackground.optional(),
  color_3: LegacyFontColor.optional(),
  color_3_bg: LegacyBackground.optional(),
  custom_css: z.string().max(65_535).optional(),
}).passthrough();
export const DesignLogo = z.object({
  type: z.enum(['text', 'image', 'html']),
  /** Bei Typ html ganze Tags, auch Data-URLs (Legacy design_logo_text TEXT, 65.535 Byte). */
  text: z.string().max(65_535).optional(),
  /** https-URL oder übergangsweise Data-URL (data:image/…) bis 2 MB aus dem Altsystem; der Import wandelt sie in Medien (`logoMediaId`) um. */
  url: z.string().max(2_000_000).refine(u => /^https?:\/\//.test(u) || /^data:image\/(png|jpeg|gif|webp);base64,/.test(u), 'http(s) oder data:image').optional(),
  stretch: z.boolean().optional(),
  paddingTop: z.number().int().min(0).max(500).optional(),
  paddingBottom: z.number().int().min(0).max(500).optional(),
});

export const Design = z.object({
  /** `null` für mandantenlose Vorlagen (Legacy user_id 8, Präfix COLTPL). */
  tenantId: TenantId.nullable(),
  designId: z.string(),
  name: z.string().min(1).max(120),
  /** Legacy-Quellkonto (Mehrkonten-Mandant); fehlt = Primärquelle. */
  legacyU: LegacyU.optional(),
  layout: z.literal('3line_header'),
  /** Legacy design_color_layout 1–15; bestimmt das gesamte CSS. Fehlt = Vorlage entscheidet. */
  variant: z.number().int().min(1).max(15).optional(),
  colors: DesignColors,
  palette: z.array(z.string().regex(/^[0-9a-f]{6}$/)).max(16).optional(),
  logo: DesignLogo.optional(),
  copyrightText: z.string().max(255).optional(),
  trustAlign: z.enum(['left', 'center', 'right']).optional(),
  /** Kunden-Zusatzcode; der Renderer sanitisiert. */
  /** Grenzen = Legacy-TEXT (65.535 Byte), damit kein importiertes Design feldweise oder ganz herausfällt (Opus-1, 24.10.2026). */
  addCode: z.object({ css: z.string().max(65_535).optional(), top: z.string().max(65_535).optional(), bottom: z.string().max(65_535).optional() }).optional(),
  boxes: z.array(DesignBox).max(50),
  links: z.array(DesignLink).max(50),
  trust: z.array(DesignTrust).max(20),
  responsive: z.record(z.string(), z.unknown()).optional(),
  logoMediaId: z.string().optional(),
  isTemplate: z.boolean().default(false),
  /** Vorlagen (tenantId null, PK TEMPLATES): Legacy-COLTPL-Designs des Systemnutzers 8; eine Vorlage liefert nur Farben und Variante, das Grundgerüst ist fest. */
  templateCategory: z.string().max(80).optional(),
  /** Aus welcher Vorlage das Design erzeugt wurde (nur Information). */
  sourceTemplateId: z.string().max(64).optional(),
  version: Version,
  /** Zuletzt veröffentlichte Revision (DESIGNREV#); fehlt = noch nie veröffentlicht. */
  publishedVersion: Version.optional(),
  deleted: z.boolean().default(false),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Design = z.infer<typeof Design>;

/**
 * Unveränderliche veröffentlichte Revision (Codex-Anfrage Phase 3, 23.10.2026): Der Worker
 * liest beim Revise genau `ARTICLEREV#<a>#<v>` bzw. `DESIGNREV#<d>#<v>` mit erwarteter
 * Version (CAS), nie die aktuellen Entwurfsblöcke. `Article.publishedVersion` und
 * `Design.publishedVersion` zeigen auf die jeweils gültige Revision.
 */
export const ArticleRevision = z.object({
  tenantId: TenantId,
  articleId: z.string(),
  version: Version,
  title: z.string().min(1).max(255),
  designId: z.string().optional(),
  /**
   * Festlegung 24.10.2026: Beim Veröffentlichen des Artikels wird die damals veröffentlichte
   * Designversion unveränderlich gebunden; der Worker rendert immer `DESIGNREV#<designId>#<designVersion>`,
   * nie den aktuellen Design-Zeiger. Ein später veröffentlichtes Design wirkt erst mit der
   * nächsten Artikelveröffentlichung. Import setzt den Rohwert aus dem Altsystem. Ohne Wert
   * (Altbestand vor dieser Regel) gilt `Embed.designVersion`, sonst die zum `publishedAt`
   * jüngste Designrevision. Ab v0.2 Pflicht, wenn designId gesetzt ist.
   */
  designVersion: Version.optional(),
  blocks: z.array(Block).max(200),
  blocksObjectKey: z.string().optional(),
  publishedAt: IsoDateTime,
  publishedBy: z.string().max(64).optional(),
});
export type ArticleRevision = z.infer<typeof ArticleRevision>;
export const DesignRevision = z.object({
  tenantId: TenantId.nullable(),
  designId: z.string(),
  version: Version,
  snapshot: z.lazy(() => Design),
  publishedAt: IsoDateTime,
  publishedBy: z.string().max(64).optional(),
});
export type DesignRevision = z.infer<typeof DesignRevision>;

/** ULID mit Präfix m (Stufe G, 24.10.2026); importierte Medien deterministisch aus media_upload_created + media_upload_id. */
export const MediaId = z.string().regex(/^m[0-9A-HJKMNP-TV-Z]{26}$/);
/** `<hostingDir>/(p|u)/<name>`; Präfix = Tenant.hostingDir, Ordner = Media.folder (mediaObjectKeyMatches). */
export const MediaObjectKey = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}\/(p|u)\/[A-Za-z0-9._-]{1,200}$/);
export const Media = z.object({
  tenantId: TenantId,
  /** Neue Medien: MediaId (ULID). Altbestand vor Stufe G darf noch freie IDs tragen; ab v0.2 MediaId. */
  mediaId: z.string().min(1).max(64),
  /** S3-Schlüssel im Hosting-Bucket der Stage; öffentliche URL = <Medienbasis der Stage>/<key> (prod: https://h.patcdn.net/). */
  objectKey: z.string().min(1).max(512),
  folder: z.enum(['p', 'u']),
  contentType: z.string().max(64),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  bytes: z.number().int().nonnegative().optional(),
  name: z.string().max(255).optional(),
  legacyMediaId: z.string().max(32).optional(),
  /** Legacy-Quellkonto (Mehrkonten-Mandant); fehlt = Primärquelle; bestimmt den hostingDir-Präfix des objectKey. */
  legacyU: LegacyU.optional(),
  sha256: Sha256Hex.optional(),
  /** upload = Stufe G, legacy = Import media_upload, inline-import = aus Data-URLs des Imports erzeugt. */
  source: z.enum(['upload', 'legacy', 'inline-import']).optional(),
  /** Weiches Ausblenden statt Löschen (Stufe G); ausgeblendete Medien bleiben in Angeboten gültig. */
  hiddenAt: IsoDateTime.optional(),
  createdBy: z.string().max(64).optional(),
  version: Version.optional(),
  createdAt: IsoDateTime,
});
export type Media = z.infer<typeof Media>;
/** Prüft, dass ein Objektschlüssel zum Hostingordner des Mandanten und zum Ordner passt. */
export function mediaObjectKeyMatches(objectKey: string, hostingDir: string, folder: 'p' | 'u'): boolean {
  return MediaObjectKey.safeParse(objectKey).success && objectKey.startsWith(`${hostingDir}/${folder}/`);
}

export const MediaUploadState = z.enum(['pending', 'processing', 'ready', 'rejected', 'expired']);
export const MediaUploadError = z.enum(['too-large', 'type-mismatch', 'decode-failed', 'dimensions', 'quota', 'malware', 'internal']);
/**
 * Upload-Vorgang (Stufe G): Browser lädt per signiertem POST in den privaten Staging-Bucket der
 * Stage; der Verarbeitungs-Lambda prüft Größe/Typ, kodiert neu (p: Bild bis 1600 px, Format
 * erhalten; u: PNG-Transparenz erhalten), schreibt in den Hosting-Bucket (prod: Rolle im Legacy-
 * Konto, nur PutObject auf `<hostingDir>/(p|u)/*` mit public-read, kein Delete/List) und schließt
 * die Zeile per CAS ab. Schlüssel keys.mediaUpload, TTL expiresAt. Idempotenz über
 * `APPCREATE#MEDIA#sha256(requestId)` (24 h).
 */
export const MediaUpload = z.object({
  tenantId: TenantId,
  uploadId: z.string().min(1).max(64),
  mediaId: MediaId,
  requestHash: Sha256Hex,
  folder: z.enum(['p', 'u']),
  declaredType: z.enum(['image/jpeg', 'image/png', 'image/gif', 'image/webp']),
  declaredBytes: z.number().int().positive().max(15 * 1024 * 1024),
  name: z.string().min(1).max(255),
  stagingKey: z.string().min(1).max(512),
  objectKey: MediaObjectKey,
  state: MediaUploadState,
  code: MediaUploadError.optional(),
  leaseUntil: IsoDateTime.optional(),
  /** Aktiver Claim des Verarbeitungs-Lambda (siehe MediaUploadClaim); fehlt = nicht beansprucht. */
  claim: z.object({ token: z.string().min(16).max(64), /** Upload.version zum Zeitpunkt des Claims; bleibt für die Claim-Epoche fest, auch wenn bindResult die Zeilenversion erhöht. */ claimVersion: Version, owner: z.string().min(1).max(128), claimedAt: IsoDateTime, leaseUntil: IsoDateTime, stagingVersionId: z.string().min(1).max(128), profile: z.enum(['p-1600-preserve', 'u-preserve']) }).optional(),
  /** Ergebnis vor dem Hosting-Put dauerhaft gebunden; erst danach Put, dann Commit, dann Staging-Löschung. */
  result: z.object({ sha256: Sha256Hex, bytes: z.number().int().positive(), width: z.number().int().positive(), height: z.number().int().positive(), contentType: z.string().max(64) }).optional(),
  /**
   * Eingabebindung des internen Import-Ports (Codex, 25.10.2026), serverseitig, nie aus der App: SHA-256 der echten
   * Stagingbytes, deren exakte Versions-ID und der Hash der persistierten privaten Admission. Bei `source: 'inline-import'`
   * prüft data das Objekt zwingend gegen Admission und Stagingbytes, bevor es claimt; `result.sha256` bleibt der kodierte Output.
   * Keine Autorisierung allein aus source, Ereignis oder IAM. Bei `upload` fehlt das Feld.
   */
  input: z.object({ sha256: Sha256Hex, stagingVersionId: z.string().min(1).max(128), admissionSha256: Sha256Hex }).optional(),
  /** Herkunft, serverseitig gesetzt (Admission = 'upload', Import-Port = 'inline-import'); die App darf das Feld nicht übergeben. Fehlt (Altzeilen) = 'upload'. Wird in Claim/Result/Receipt unverändert mitgebunden und landet als Media.source. */
  source: z.enum(['upload', 'inline-import']).default('upload'),
  createdBy: z.string().max(64),
  createdAt: IsoDateTime,
  expiresAt: IsoDateTime,
  version: Version,
});
export type MediaUpload = z.infer<typeof MediaUpload>;
/**
 * Claim-Vertrag Verarbeitungs-Lambda (Codex 24.10.2026). `claimMediaUpload` setzt per CAS
 * (state pending → processing, version+1) `claim` und gibt dieses DTO zurück; `completeMediaUpload`
 * und `rejectMediaUpload` verlangen genau dieses DTO: Tenant, Upload, Token und `claimVersion`
 * müssen mit der gespeicherten Zeile übereinstimmen, die Lease darf nicht abgelaufen sein.
 * Ablauf: Staging-Objekt exakt in `stagingVersionId` lesen → dekodieren → `result` binden (CAS) →
 * Hosting-Put mit If-None-Match und public-read → MEDIA#/MEDIALIST#/MEDIASTAT/UPLOAD ready in einer
 * Transaktion → erst danach Staging-Version löschen und Ereignis bestätigen. Zielschlüssel steht
 * beim Anlegen fest: Endung aus dem deklarierten Typ, Format bleibt erhalten (p und u: Kante ≤ 1600 px,
 * beide erhalten Alpha bei PNG/WebP/GIF, keine Hintergrundfüllung; GIF, APNG und animiertes WebP: nur das erste Bild im deklarierten Format; APNG mit
 * separater Posterfläche → decode-failed). Kein Umbenennen nach dem Decode.
 */
export const MediaUploadClaim = z.object({
  tenantId: TenantId,
  uploadId: z.string().min(1).max(64),
  token: z.string().min(16).max(64),
  claimVersion: Version,
  owner: z.string().min(1).max(128),
  leaseUntil: IsoDateTime,
  stagingKey: z.string().min(1).max(512),
  stagingVersionId: z.string().min(1).max(128),
  objectKey: MediaObjectKey,
  profile: z.enum(['p-1600-preserve', 'u-preserve']),
  /** Eingabebindung aus MediaUpload.input, unveränderlich im Claim; Pflicht bei `inline-import` (data erzwingt es). */
  input: z.object({ sha256: Sha256Hex, stagingVersionId: z.string().min(1).max(128), admissionSha256: Sha256Hex }).optional(),
  /** Herkunft aus MediaUpload.source, unveränderlich im Claim. */
  source: z.enum(['upload', 'inline-import']).default('upload'),
  expiresAt: IsoDateTime,
});
export type MediaUploadClaim = z.infer<typeof MediaUploadClaim>;
/** Indexzeile unter keys.mediaList; trägt nur, was die Liste braucht. */
export const MediaListEntry = z.object({
  tenantId: TenantId,
  mediaId: z.string().min(1).max(64),
  folder: z.enum(['p', 'u']),
  createdAt: IsoDateTime,
  hidden: z.boolean().default(false),
});
export type MediaListEntry = z.infer<typeof MediaListEntry>;
/**
 * Sichtbare Zähler je Ordner (keys.mediaStat, 24.10.2026). `catalogComplete` wird erst true, wenn
 * ein belegter Vollabgleich (Import oder Nachzählung) alle MEDIA#-Zeilen indiziert hat; vorher
 * zeigt die App „Zahl wird ermittelt“. Fehlt die Zeile, gilt {p:0,u:0} nur nach einer
 * ungefilterten MEDIA#-Abfrage mit Count 0 und ohne Cursor. Upload/Import/Ausblenden schreiben
 * MEDIA#, MEDIALIST# und MEDIASTAT atomar; `version` ist das CAS-Feld.
 */
export const MediaStat = z.object({
  tenantId: TenantId,
  p: z.number().int().nonnegative(),
  u: z.number().int().nonnegative(),
  hidden: z.number().int().nonnegative().default(0),
  catalogComplete: z.boolean(),
  catalogCheckedAt: IsoDateTime.optional(),
  version: Version,
  updatedAt: IsoDateTime,
});
export type MediaStat = z.infer<typeof MediaStat>;
/** Grenzen Stufe G (Vorschlag Opus-1, von Claude 24.10.2026 als Standard gesetzt; Flori kann anpassen). */
export const mediaLimits = { maxInputBytes: 15 * 1024 * 1024, maxInputPixels: 50_000_000, uploadsPerDay: 200, maxEdgePx: 1600 } as const;

// ---------------------------------------------------------------------------
// Jobs, Status, Migration
// ---------------------------------------------------------------------------

export const JobType = z.enum([
  'sync.reconcile',
  'sync.delta',
  'item.details',
  'store.sync',
  'rules.evaluate',
  'description.reconcile',
  'description.revise',
  'token.check',
  'tenant.import',
  /** Täglicher Kontodaten-/Rechnungs-Nachlauf (Runbook 9a, 25.10.2026): status-only, operatorgeführt, kein Queue-Pfad; `running` bis zum Abschluss-Receipt, bei Abbruch `failed` mit `lastError.code = 'followup-incomplete'` (Code wie in der App, Opus-1), Zähler `missing-in-source`, `skipped-<grund>`, `invoices-new`, `invoices-changed`. Überschreibt nie den Status von `tenant.import`. */
  'account.followup',
  'category.sync',
]);
export type JobType = z.infer<typeof JobType>;

export const JobStatus = z.object({
  tenantId: TenantId,
  type: JobType,
  state: z.enum(['idle', 'running', 'failed', 'paused']),
  lastRunAt: IsoDateTime.optional(),
  lastSuccessAt: IsoDateTime.optional(),
  nextRunAt: IsoDateTime.optional(),
  counters: z.record(z.string(), z.number().int().nonnegative()).default({}),
  lastError: z.object({ at: IsoDateTime, code: z.string().max(64), message: z.string().max(500) }).optional(),
});
export type JobStatus = z.infer<typeof JobStatus>;

/**
 * Atomarer Anspruch auf einen Lauf: Bedingungsschreiben auf owner/leaseUntil.
 * FIFO je Konto serialisiert, verhindert aber keine Einschübe zwischen den Seiten
 * eines Laufs; die Lease macht den Lauf gegen parallele Starter und Wiederholungen dicht.
 */
/** Artikelgruppe (Legacy pat_main.artikel_gruppen): reine Ordnung der Artikelliste. Schlüssel keys.articleGroup. */
export const ArticleGroup = z.object({
  tenantId: TenantId,
  groupId: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  order: z.number().int().nonnegative().optional(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ArticleGroup = z.infer<typeof ArticleGroup>;

/**
 * Outbox-Eintrag (Festlegung 24.10.2026): Auftragsabsicht aus App-Transaktionen. Schlüssel keys.outbox.
 * Der Dispatcher (Codex) überführt `pending` in eine Jobnachricht und setzt `done` mit `jobId`;
 * `attempts` zählt Zustellversuche, ab 5 geht der Eintrag in `failed` und erscheint im Status.
 */
export const OutboxEntry = z.object({
  tenantId: TenantId,
  outboxId: z.string().min(1).max(64),
  kind: z.enum(['description.reconcile', 'sync.reconcile', 'rules.evaluate']),
  payload: z.record(z.string(), z.unknown()),
  /** 'gallery-delete' (25.10.2026, Entscheidung 10): Löschen einer Galerie erzeugt `description.reconcile` mit payload.reason 'gallery-deleted'; Identität `{galleryId, version, domain:'gallery-deleted'}`. */
  reason: z.enum(['publish', 'assign', 'unassign', 'gallery-save', 'gallery-delete', 'user-change']),
  createdAt: IsoDateTime,
  state: z.enum(['pending', 'done', 'failed']).default('pending'),
  attempts: z.number().int().nonnegative().default(0),
  jobId: z.string().max(128).optional(),
  doneAt: IsoDateTime.optional(),
});
export type OutboxEntry = z.infer<typeof OutboxEntry>;

export const Lease = z.object({
  owner: z.string().min(1).max(128),
  leaseUntil: IsoDateTime,
  heartbeatAt: IsoDateTime,
});
export type Lease = z.infer<typeof Lease>;

/**
 * Zustand eines mehrteiligen Laufs (Reconcile-Seiten, Delta-Teilfenster). Der
 * Wasserstand des Kontos und das Beenden nicht gesehener Items werden erst
 * geschrieben, wenn state = 'complete'. Schlüssel: keys.syncRun.
 */
export const SyncRun = z.object({
  tenantId: TenantId,
  runId: z.string().min(8).max(64),
  type: z.enum(['sync.reconcile', 'sync.delta']),
  state: z.enum(['running', 'complete', 'failed', 'abandoned']),
  lease: Lease.optional(),
  /** Reconcile: nächste Seite; Delta: nächstes Teilfenster. */
  cursor: z.object({
    page: z.number().int().positive().optional(),
    site: EbaySiteId.optional(),
    windowFrom: IsoDateTime.optional(),
    windowTo: IsoDateTime.optional(),
    split: z.number().int().min(0).max(12).optional(),
  }),
  pagesDone: z.number().int().nonnegative().default(0),
  itemsSeen: z.number().int().nonnegative().default(0),
  startedAt: IsoDateTime,
  finishedAt: IsoDateTime.optional(),
  lastError: z.object({ at: IsoDateTime, code: z.string().max(64), message: z.string().max(500) }).optional(),
  /** TTL in Sekunden seit Epoche, z. B. 14 Tage. */
  expiresAtEpoch: z.number().int().positive(),
});
export type SyncRun = z.infer<typeof SyncRun>;

export const JobRun = z.object({
  tenantId: TenantId,
  type: JobType,
  startedAt: IsoDateTime,
  finishedAt: IsoDateTime.optional(),
  /** 'skipped-paused': Dispatcher hat den Job wegen `SyncState.pauseReason` verworfen (kein Fehler), 24.10.2026. */
  outcome: z.enum(['ok', 'partial', 'failed', 'skipped-paused']).optional(),
  counters: z.record(z.string(), z.number().int().nonnegative()).default({}),
  /** TTL in Sekunden seit Epoche für DynamoDB. */
  expiresAtEpoch: z.number().int().positive(),
});
export type JobRun = z.infer<typeof JobRun>;

/** Statusdatensatz für die Startseite, vom Worker geschrieben (Opus-Anfrage 5). Schlüssel keys.tenantStats. */
export const TenantStats = z.object({
  tenantId: TenantId,
  activeItems: z.number().int().nonnegative(),
  activeItemsBySite: z.record(z.string().regex(/^[0-9]{1,3}$/), z.number().int().nonnegative()),
  itemsWithReviseError: z.number().int().nonnegative(),
  pendingRevisions: z.number().int().nonnegative(),
  computedAt: IsoDateTime,
});
export type TenantStats = z.infer<typeof TenantStats>;

/** Laufender Auth'n'Auth-Verbindungsvorgang; SessionID gilt 5 Minuten, nie loggen. Schlüssel keys.ebayConnect. */
export const EbayConnectSession = z.object({
  tenantId: TenantId,
  sessionId: z.string().min(1).max(128),
  requestedBy: z.string().min(1).max(64),
  createdAt: IsoDateTime,
  expiresAt: IsoDateTime,
  expiresAtEpoch: z.number().int().positive(),
});
export type EbayConnectSession = z.infer<typeof EbayConnectSession>;

/**
 * Historisches Format der globalen Verkäuferzuordnung (bis 31.10.2026).
 * Entscheidung Flori: Ein Mandant hat einen aktuellen Verkäufer, derselbe Verkäufer
 * darf mehreren Mandanten gehören. Verbindlich ist ausschließlich Tenant.ebay;
 * alte globale Zeilen werden weder für Exklusivität gelesen noch verändert.
 * @deprecated Nur zum Lesen historischer Daten, nicht für Verbindungsentscheidungen.
 */
export const EbayUserBinding = z.object({
  /** Ab v0.2 Pflicht; bis dahin bindet pat-app über den Namen. */
  ebayUserId: z.string().min(1).max(128).optional(),
  ebayUserName: z.string().min(1).max(64),
  tenantId: TenantId,
  boundAt: IsoDateTime,
  source: z.enum(['get-user', 'legacy-name']).optional(),
});
export type EbayUserBinding = z.infer<typeof EbayUserBinding>;

/** Zuordnung der öffentlichen Kennungen u/g zu Mandant und Galerie. */
export const LegacyMapping = z.object({
  u: LegacyU,
  g: LegacyG.optional(),
  tenantId: TenantId,
  galleryId: z.string().optional(),
  /** 'eingestellt': Legacy-Galerietyp 2 (Navigation) und andere ausgemusterte Varianten; Edge liefert leeres CSS mit 200, keine Daten. */
  state: z.enum(['alt', 'migriert', 'eingestellt']),
});
export type LegacyMapping = z.infer<typeof LegacyMapping>;

/**
 * Mehrkonten-Mandant (Flori 29.10.2026): ein Zielmandant bündelt mehrere Legacy-Konten (z. B. zwei eBay-Konten
 * eines Kunden). Die Primärquelle bleibt `Tenant.legacyU`/`Tenant.hostingDir`; weitere Quellen sind sekundär.
 * Besitz jeder Quelle belegt der bestehende Zeiger `keys.legacyTenant(u)` (LegacyMapping.tenantId), den der Import
 * auch für sekundäre u schreibt. Inhalte tragen ihre Quelle als `legacyU` (Gallery, Media, Design); fehlt = primär.
 * Keine freie Prefixliste, kein Durchprobieren aller u: genau ein serverseitiger Resolver (Data) liefert die Quelle.
 */
export const TenantLegacySource = z.object({
  legacyU: LegacyU,
  hostingDir: HostingDir.optional(),
  /** Legacy-Nutzer-ID (pat_main.user.id) als Dezimalstring. */
  legacyAccountId: DecimalId,
  role: z.enum(['primary', 'secondary']),
  importedAt: IsoDateTime,
  importRunId: z.string().min(1).max(64),
});
export type TenantLegacySource = z.infer<typeof TenantLegacySource>;

export const TenantLegacySources = z.object({
  tenantId: TenantId,
  version: Version,
  sources: z.array(TenantLegacySource).min(1).max(5),
  updatedAt: IsoDateTime,
}).superRefine((value, ctx) => {
  const primaries = value.sources.filter(source => source.role === 'primary').length;
  if (primaries !== 1) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'exactly one primary source' });
  const us = new Set(value.sources.map(source => source.legacyU));
  if (us.size !== value.sources.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'legacyU must be unique' });
  const dirs = value.sources.map(source => source.hostingDir).filter((dir): dir is string => dir !== undefined);
  if (new Set(dirs).size !== dirs.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'hostingDir must be unique' });
});
export type TenantLegacySources = z.infer<typeof TenantLegacySources>;

/** Quelle zu einem Inhalt: `legacyU` gesetzt → genau diese Quelle (oder undefined, wenn nicht gebunden); fehlend → Primärquelle. */
export function resolveLegacySource(sources: TenantLegacySources, legacyU?: string): TenantLegacySource | undefined {
  if (legacyU === undefined) return sources.sources.find(source => source.role === 'primary');
  return sources.sources.find(source => source.legacyU === legacyU);
}

export const StoreCategory = z.object({
  id: z.string().max(20),
  name: z.string().max(255),
  parentId: z.string().max(20).optional(),
  level: z.number().int().min(1).max(3),
  order: z.number().int().nonnegative().optional(),
  /** Anzahl aktiver Angebote in der Kategorie (Legacy ebay_store_category_items); füllt store.sync aus dem Cache. */
  itemCount: z.number().int().nonnegative().optional(),
});
/**
 * Shopkategorien eines Kontos mit explizitem Abonnementzustand (Anfrage Codex,
 * 23.10.2026): `GetStore` liefert bei Konten ohne Shop den Fehlercode 13003. Dann
 * ist `subscription` = 'none' und `categories` leer; ein zuvor gefüllter Cache wird
 * nicht stillschweigend überschrieben, sondern über `previousCategoriesRetainedUntil`
 * sichtbar behalten, bis fachlich entschieden ist, ob er geleert wird.
 */
export const StoreSubscription = z.enum(['subscribed', 'none', 'unknown']);
export const StoreCategories = z.object({
  tenantId: TenantId,
  /** Fehlt = 'unknown'. Ab v0.2 Pflicht. */
  subscription: StoreSubscription.optional(),
  categories: z.array(StoreCategory).max(5000),
  fetchedAt: IsoDateTime,
  /** Gesetzt, wenn `subscription` auf 'none' wechselte, der alte Cache aber bewusst noch gilt. */
  previousCategoriesRetainedUntil: IsoDateTime.optional(),
  /** Letzter eBay-Fehlercode des Abrufs, z. B. '13003'. */
  lastErrorCode: z.string().max(16).optional(),
});
export type StoreCategories = z.infer<typeof StoreCategories>;

export const EbayCategory = z.object({
  site: EbaySiteId,
  id: z.string().max(20),
  name: z.string().max(255),
  parentId: z.string().max(20).optional(),
  level: z.number().int().min(1).max(10),
  /** TTL in Sekunden seit Epoche. */
  expiresAtEpoch: z.number().int().positive(),
});
export type EbayCategory = z.infer<typeof EbayCategory>;
